/**
 * Pricing APIs
 * - Analytics: /pricing/analytics (demand, forecast, suggested prices, history)
 * - Preview:   /pricing/preview  (suggested prices without applying)
 * - Signals:   /pricing/signals  (competitor_price_index, event_weather_index)
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp > currentTime) return tokens.idToken.toString();
    }
  } catch (e) {
    console.error('Pricing API: auth error', e);
  }
  return null;
};

/**
 * Get pricing preview (suggested prices without applying).
 * GET /pricing/preview
 * @param {Object} params - Parameters
 * @param {string} [params.site_id] - Site ID (optional, default: "default")
 * @param {string} [params.time_bucket] - Time bucket (e.g. "2026-02-03T10:00") (optional, default: current hour UTC)
 */
export const getPricingPreview = async ({ site_id, dish_id, time_bucket } = {}) => {
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  if (dish_id) params.set('dish_id', dish_id);
  if (time_bucket) params.set('time_bucket', time_bucket);

  try {
    const url = `${API_BASE_URL}/pricing/preview?${params.toString()}`;
    console.log('👁️ getPricingPreview Request:', { url, params: { site_id, dish_id, time_bucket } });
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    //console.log('👁️ getPricingPreview Response Status:', res.status, res.statusText);
    if (res.ok) {
      const data = await res.json();
      //console.log('👁️ getPricingPreview Response Data:', data);
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_pricing_dashboard',
        `Successfully fetched pricing preview`,
        {
          endpoint: '/pricing/preview',
          method: 'GET',
          site_id,
          dish_id,
          time_bucket,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    const errText = await res.text();
    //console.error('👁️ getPricingPreview Error Response:', { status: res.status, errText });
    
    const error = new Error(`Failed to load pricing preview: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/preview';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API error: ${res.status} ${res.statusText} for getPricingPreview`,
      error,
      {
        endpoint: '/pricing/preview',
        method: 'GET',
        site_id,
        dish_id,
        time_bucket,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: 'Failed to load pricing preview', data: null };
  } catch (err) {
    //console.error('👁️ getPricingPreview Exception:', err);
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API network/fetch error for getPricingPreview`,
      err,
      {
        endpoint: '/pricing/preview',
        method: 'GET',
        site_id,
        dish_id,
        time_bucket,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message, data: null };
  }
};

/**
 * Get pricing analytics (demand, forecast, signals, suggested prices, history).
 * GET /pricing/analytics
 * @param {Object} params - Parameters
 * @param {string} [params.site_id] - Site ID (optional, default: "default")
 * @param {string} [params.dish_id] - Dish ID for per-dish charts (optional)
 * @param {string} [params.time_bucket] - Time bucket (e.g. "2026-02-03T10:00") (optional, default: current hour UTC)
 */
export const getPricingAnalytics = async ({ site_id, dish_id, time_bucket } = {}) => {
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  if (dish_id) params.set('dish_id', dish_id);
  if (time_bucket) params.set('time_bucket', time_bucket);

  try {
    const url = `${API_BASE_URL}/pricing/analytics?${params.toString()}`;
    console.log('📈 getPricingAnalytics Request:', { url, params: { site_id, dish_id, time_bucket } });
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    console.log('📈 getPricingAnalytics Response Status:', res.status, res.statusText);
    if (res.ok) {
      const data = await res.json();
      console.log('📈 getPricingAnalytics Response Data:', data);
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_pricing_dashboard',
        `Successfully fetched pricing analytics`,
        {
          endpoint: '/pricing/analytics',
          method: 'GET',
          site_id,
          dish_id,
          time_bucket,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    const errText = await res.text();
    console.error('📈 getPricingAnalytics Error Response:', { status: res.status, errText });
    
    const error = new Error(`Failed to load analytics: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/analytics';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API error: ${res.status} ${res.statusText} for getPricingAnalytics`,
      error,
      {
        endpoint: '/pricing/analytics',
        method: 'GET',
        site_id,
        dish_id,
        time_bucket,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Failed to load analytics', data: null };
  } catch (err) {
    console.error('📈 getPricingAnalytics Exception:', err);
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API network/fetch error for getPricingAnalytics`,
      err,
      {
        endpoint: '/pricing/analytics',
        method: 'GET',
        site_id,
        dish_id,
        time_bucket,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error', data: null };
  }
};

/**
 * Trigger refresh prices (on-demand; backend may no-op if price application is off).
 * POST /pricing/update
 * Note: Request body is optional/ignored per API docs (currently no-op/analytics-only mode).
 * @param {Object} [params] - Parameters (ignored, kept for backward compatibility)
 * @param {string} [params.site_id] - Site ID (ignored by backend)
 */
export const refreshPricesNow = async (params = {}) => {
  const token = await getAuthToken();
  try {
    const url = `${API_BASE_URL}/pricing/update`;
    console.log('🔄 refreshPricesNow Request:', { url, params });
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      // Body is optional per API docs - backend ignores it, so we don't send it
    });
    // console.log('🔄 refreshPricesNow Response Status:', res.status, res.statusText);
    if (res.ok) {
      const data = await res.json();
      // console.log('🔄 refreshPricesNow Response Data:', data);
      
      // Log API event for successful refresh
      loggingService.logInfo(
        'api_success_pricing_dashboard',
        `Successfully refreshed prices`,
        {
          endpoint: '/pricing/update',
          method: 'POST',
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    const errText = await res.text();
    // console.error('🔄 refreshPricesNow Error Response:', { status: res.status, errText });
    
    const error = new Error(`Refresh failed: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/update';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API error: ${res.status} ${res.statusText} for refreshPricesNow`,
      error,
      {
        endpoint: '/pricing/update',
        method: 'POST',
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Refresh failed' };
  } catch (err) {
    // console.error('🔄 refreshPricesNow Exception:', err);
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API network/fetch error for refreshPricesNow`,
      err,
      {
        endpoint: '/pricing/update',
        method: 'POST',
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error' };
  }
};

// ——— Restaurant / Dish (for Pricing Rules dish dropdown) ———

/**
 * POST /restaurant/dish/getall-by-site?site_id=... — get dishes by site.
 * Mirrors Flutter getDishesBySite(siteId).
 */
export const getDishesBySite = async (siteId) => {
  const token = await getAuthToken();
  if (!token) {
    return { success: false, message: 'Authentication token not found', data: null };
  }
  try {
    const url = `${API_BASE_URL}/restaurant/dish/getall-by-site?site_id=${encodeURIComponent(siteId)}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.status === 200) {
      const data = await res.json();
      const dishes = data.data ?? data.dishes ?? data;
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully fetched dishes for site ${siteId}`,
        {
          endpoint: '/restaurant/dish/getall-by-site',
          method: 'POST',
          siteId,
          dishesCount: Array.isArray(dishes) ? dishes.length : 0,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: dishes };
    }
    const errText = await res.text();
    
    const error = new Error(`Failed to retrieve dishes: ${res.status} - ${errText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/restaurant/dish/getall-by-site';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for getDishesBySite`,
      error,
      {
        endpoint: '/restaurant/dish/getall-by-site',
        method: 'POST',
        siteId,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return {
      success: false,
      message: `Failed to retrieve dishes: ${res.status} - ${errText}`,
      data: null,
    };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for getDishesBySite`,
      err,
      {
        endpoint: '/restaurant/dish/getall-by-site',
        method: 'POST',
        siteId,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return {
      success: false,
      message: err?.message || 'Network error',
      data: null,
    };
  }
};

/**
 * GET /restaurant/dish?dish_id=... — get dish by ID.
 */
export const getDishById = async (dishId) => {
  const token = await getAuthToken();
  if (!token) {
    return { success: false, message: 'Authentication token not found', data: null };
  }
  if (!dishId) {
    return { success: false, message: 'Missing required query parameter dish_id', data: null };
  }
  try {
    const url = `${API_BASE_URL}/restaurant/dish?dish_id=${encodeURIComponent(dishId)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.status === 200) {
      const data = await res.json();
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully fetched dish ${dishId}`,
        {
          endpoint: '/restaurant/dish',
          method: 'GET',
          dishId,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    if (res.status === 404) {
      return { success: false, message: `Dish with ID '${dishId}' not found`, data: null };
    }
    const errText = await res.text();
    
    const error = new Error(`Failed to retrieve dish: ${res.status} - ${errText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/restaurant/dish';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for getDishById`,
      error,
      {
        endpoint: '/restaurant/dish',
        method: 'GET',
        dishId,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return {
      success: false,
      message: `Failed to retrieve dish: ${res.status} - ${errText}`,
      data: null,
    };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for getDishById`,
      err,
      {
        endpoint: '/restaurant/dish',
        method: 'GET',
        dishId,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return {
      success: false,
      message: err?.message || 'Network error',
      data: null,
    };
  }
};

// ——— Pricing Rules CRUD (dish-pricing-rules) ———

/**
 * GET /pricing/rules — list rules (optional site_id filter).
 */
export const getPricingRules = async ({ site_id } = {}) => {
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  try {
    const url = `${API_BASE_URL}/pricing/rules?${params.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (res.ok) {
      const data = await res.json();
      const rules = data.data ?? data.rules ?? data;
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully fetched pricing rules`,
        {
          endpoint: '/pricing/rules',
          method: 'GET',
          site_id,
          rulesCount: Array.isArray(rules) ? rules.length : 0,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: rules };
    }
    
    const errText = await res.text().catch(() => '');
    const error = new Error(`Failed to load rules: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/rules';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for getPricingRules`,
      error,
      {
        endpoint: '/pricing/rules',
        method: 'GET',
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: 'Failed to load rules', data: null };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for getPricingRules`,
      err,
      {
        endpoint: '/pricing/rules',
        method: 'GET',
        site_id,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error', data: null };
  }
};

/**
 * GET /pricing/rules/{dish_id} — get single rule.
 * @param {Object} params - Parameters
 * @param {string} params.dish_id - Dish identifier (required)
 * @param {string} [params.site_id] - Site ID (optional, default: "*")
 */
export const getPricingRule = async ({ dish_id, site_id } = {}) => {
  if (!dish_id) {
    return { success: false, message: 'dish_id is required', data: null };
  }
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  const queryString = params.toString();
  const url = `${API_BASE_URL}/pricing/rules/${encodeURIComponent(dish_id)}${queryString ? `?${queryString}` : ''}`;
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (res.ok) {
      const data = await res.json();
      const rule = data.data ?? data.rule ?? data;
      
      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully fetched pricing rule for dish ${dish_id}`,
        {
          endpoint: `/pricing/rules/${dish_id}`,
          method: 'GET',
          dish_id,
          site_id,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: Array.isArray(rule) ? rule[0] : rule };
    }
    if (res.status === 404) {
      return { success: false, message: 'Rule not found', data: null };
    }
    const errText = await res.text();
    
    const error = new Error(`Failed to load rule: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = `/pricing/rules/${dish_id}`;
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for getPricingRule`,
      error,
      {
        endpoint: `/pricing/rules/${dish_id}`,
        method: 'GET',
        dish_id,
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Failed to load rule', data: null };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for getPricingRule`,
      err,
      {
        endpoint: `/pricing/rules/${dish_id}`,
        method: 'GET',
        dish_id,
        site_id,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error', data: null };
  }
};

/**
 * POST /pricing/rules — create rule.
 */
export const createPricingRule = async (body) => {
  const token = await getAuthToken();
  try {
    const res = await fetch(`${API_BASE_URL}/pricing/rules`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json();
      
      // Log API event for successful creation
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully created pricing rule`,
        {
          endpoint: '/pricing/rules',
          method: 'POST',
          dishId: body.dish_id,
          siteId: body.site_id,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    const errText = await res.text();
    
    const error = new Error(`Failed to create rule: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/rules';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for createPricingRule`,
      error,
      {
        endpoint: '/pricing/rules',
        method: 'POST',
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Failed to create rule' };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for createPricingRule`,
      err,
      {
        endpoint: '/pricing/rules',
        method: 'POST',
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error' };
  }
};

/**
 * PUT /pricing/rules/{dish_id} — update rule.
 * @param {Object} params - Parameters
 * @param {string} params.dish_id - Dish identifier (required)
 * @param {string} [params.site_id] - Site ID (optional, default: "*")
 * @param {Object} body - Update payload (all fields optional)
 */
export const updatePricingRule = async ({ dish_id, site_id }, body) => {
  if (!dish_id) {
    return { success: false, message: 'dish_id is required' };
  }
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  const queryString = params.toString();
  const url = `${API_BASE_URL}/pricing/rules/${encodeURIComponent(dish_id)}${queryString ? `?${queryString}` : ''}`;
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json();
      
      // Log API event for successful update
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully updated pricing rule for dish ${dish_id}`,
        {
          endpoint: `/pricing/rules/${dish_id}`,
          method: 'PUT',
          dish_id,
          site_id,
          updatedFields: Object.keys(body || {}),
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    const errText = await res.text();
    
    const error = new Error(`Failed to update rule: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = `/pricing/rules/${dish_id}`;
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for updatePricingRule`,
      error,
      {
        endpoint: `/pricing/rules/${dish_id}`,
        method: 'PUT',
        dish_id,
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Failed to update rule' };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for updatePricingRule`,
      err,
      {
        endpoint: `/pricing/rules/${dish_id}`,
        method: 'PUT',
        dish_id,
        site_id,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error' };
  }
};

/**
 * DELETE /pricing/rules/{dish_id} — delete rule.
 * @param {Object} params - Parameters
 * @param {string} params.dish_id - Dish identifier (required)
 * @param {string} [params.site_id] - Site ID (optional, default: "*")
 */
export const deletePricingRule = async ({ dish_id, site_id } = {}) => {
  if (!dish_id) {
    return { success: false, message: 'dish_id is required' };
  }
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  const queryString = params.toString();
  const url = `${API_BASE_URL}/pricing/rules/${encodeURIComponent(dish_id)}${queryString ? `?${queryString}` : ''}`;
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (res.ok) {
      // Log API event for successful deletion
      loggingService.logInfo(
        'api_success_pricing_rules',
        `Successfully deleted pricing rule for dish ${dish_id}`,
        {
          endpoint: `/pricing/rules/${dish_id}`,
          method: 'DELETE',
          dish_id,
          site_id,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true };
    }
    
    const errText = await res.text();
    const error = new Error(`Failed to delete rule: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = `/pricing/rules/${dish_id}`;
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API error: ${res.status} ${res.statusText} for deletePricingRule`,
      error,
      {
        endpoint: `/pricing/rules/${dish_id}`,
        method: 'DELETE',
        dish_id,
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Failed to delete rule' };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_rules',
      `Pricing Rules API network/fetch error for deletePricingRule`,
      err,
      {
        endpoint: `/pricing/rules/${dish_id}`,
        method: 'DELETE',
        dish_id,
        site_id,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error' };
  }
};

// ——— Pricing Signals (competitor_price_index, event_weather_index) ———

/**
 * GET /pricing/signals — get current signals for site.
 */
export const getPricingSignals = async ({ site_id } = {}) => {
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  try {
    const url = `${API_BASE_URL}/pricing/signals?${params.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (res.ok) {
      const data = await res.json();
      return { success: true, data: data.data ?? data };
    }
    // Log error but don't throw - signals are optional
    const errText = await res.text();
    
    const error = new Error(`Signals API error: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/signals';
    error.responseData = errText;
    
    // Log API error to logging service (signals are optional, but we still log for monitoring)
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API error: ${res.status} ${res.statusText} for getPricingSignals (non-blocking)`,
      error,
      {
        endpoint: '/pricing/signals',
        method: 'GET',
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
        isOptional: true, // Mark as optional/non-blocking
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    if (res.status === 500) {
      // 500 errors are backend issues - log but don't alarm user
      console.debug('📊 Signals API returned 500 (non-blocking, dashboard continues normally):', errText);
    } else {
      console.warn('⚠️ Signals API error (non-blocking):', res.status, errText);
    }
    return { success: false, message: `Signals API error: ${res.status}`, data: null };
  } catch (err) {
    // Log error but don't throw - signals are optional
    console.warn('⚠️ Signals API exception (non-blocking):', err);
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API network/fetch error for getPricingSignals (non-blocking)`,
      err,
      {
        endpoint: '/pricing/signals',
        method: 'GET',
        site_id,
        errorType: err?.name || 'NetworkError',
        isOptional: true, // Mark as optional/non-blocking
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error', data: null };
  }
};

/**
 * PUT /pricing/signals — update competitor_price_index, event_weather_index.
 * @param {Object} params - Parameters
 * @param {string} [params.site_id] - Site ID (optional, default: "default")
 * @param {string} [params.time_bucket] - Time bucket (e.g. "2026-02-03T10:00") (optional, default: current hour UTC)
 * @param {number} [params.competitor_price_index] - Competitor price index (0-1 or scale)
 * @param {number} [params.event_weather_index] - Event/weather index (0-1 or scale)
 */
export const updatePricingSignals = async ({ site_id, time_bucket, competitor_price_index, event_weather_index } = {}) => {
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  if (time_bucket) params.set('time_bucket', time_bucket);
  const queryString = params.toString();
  const url = `${API_BASE_URL}/pricing/signals${queryString ? `?${queryString}` : ''}`;
  
  // Build request body with only the signal fields
  const body = {};
  if (competitor_price_index != null) {
    body.competitor_price_index = Number(competitor_price_index);
  }
  if (event_weather_index != null) {
    body.event_weather_index = Number(event_weather_index);
  }
  
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json();
      
      // Log API event for successful update
      loggingService.logInfo(
        'api_success_pricing_dashboard',
        `Successfully updated pricing signals`,
        {
          endpoint: '/pricing/signals',
          method: 'PUT',
          site_id,
          time_bucket,
          hasCompetitorIndex: competitor_price_index != null,
          hasEventWeatherIndex: event_weather_index != null,
        }
      ).catch(logError => {
        console.warn('[pricingApi] Failed to log success event:', logError);
      });
      
      return { success: true, data: data.data ?? data };
    }
    const errText = await res.text();
    
    const error = new Error(`Failed to update signals: ${res.status} ${errText || res.statusText}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/pricing/signals';
    error.responseData = errText;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API error: ${res.status} ${res.statusText} for updatePricingSignals`,
      error,
      {
        endpoint: '/pricing/signals',
        method: 'PUT',
        site_id,
        time_bucket,
        status: res.status,
        statusText: res.statusText,
        responseData: errText,
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: errText || 'Failed to update signals' };
  } catch (err) {
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_pricing_dashboard',
      `Pricing Dashboard API network/fetch error for updatePricingSignals`,
      err,
      {
        endpoint: '/pricing/signals',
        method: 'PUT',
        site_id,
        time_bucket,
        errorType: err?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[pricingApi] Failed to log error:', logError);
    });
    
    return { success: false, message: err?.message || 'Network error' };
  }
};

// ——— Mock data for development / when backend not ready ———

function getMockPricingDashboard({ site_id, period, date, start_date, end_date }) {
  const days = period === 'monthly' ? 30 : period === 'weekly' ? 7 : 7;
  const labels = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    labels.push(d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }));
  }

  const basePrices = [120, 95, 150, 80, 250];
  const currentPrices = basePrices.map((b, i) => b + (i % 3 === 0 ? 10 : i % 3 === 1 ? -5 : 0));
  const suggestedPrices = basePrices.map((b, i) => b + Math.round((Math.random() - 0.3) * 20));

  return {
    summary: {
      dishes_auto_pricing_count: 12,
      avg_current_vs_base_percent: 5.2,
      last_run_at: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
      current_demand_intensity: 72,
      competitor_price_index: 58,
      event_weather_index: 65,
    },
    price_series: {
      labels,
      current: labels.map(() => currentPrices[0] + Math.round((Math.random() - 0.5) * 15)),
      suggested: labels.map(() => suggestedPrices[0] + Math.round((Math.random() - 0.5) * 12)),
      base: labels.map(() => basePrices[0]),
    },
    demand_series: {
      labels,
      demand_intensity: labels.map(() => Math.floor(40 + Math.random() * 50)),
      forecast_demand: labels.map(() => Math.floor(45 + Math.random() * 45)),
    },
    recent_price_changes: [
      { dish_name: 'Paneer Sandwich', old_price: 118, new_price: 122, time: '10 min ago', reason: 'Demand high' },
      { dish_name: 'Veg Burger', old_price: 98, new_price: 95, time: '12 min ago', reason: 'Schedule' },
      { dish_name: 'French Fries', old_price: 82, new_price: 85, time: '15 min ago', reason: 'Demand + weather' },
      { dish_name: 'Coffee', old_price: 50, new_price: 52, time: '18 min ago', reason: 'Demand high' },
      { dish_name: 'Pizza Margherita', old_price: 248, new_price: 252, time: '20 min ago', reason: 'Event index' },
    ],
    dish_price_chart: {
      dishes: [
        { dish_id: 'd1', dish_name: 'Paneer Sandwich', base_price: 120, current_price: 122, suggested_price: 125 },
        { dish_id: 'd2', dish_name: 'Veg Burger', base_price: 95, current_price: 95, suggested_price: 98 },
      ],
      series_labels: labels,
      current: labels.map((_, i) => 120 + Math.sin(i * 0.5) * 8),
      suggested: labels.map((_, i) => 122 + Math.sin(i * 0.5 + 0.3) * 10),
      base: labels.map(() => 120),
    },
  };
}

function getMockPricingPreview(site_id) {
  return {
    items: [
      { dish_id: 'd1', dish_name: 'Paneer Sandwich', current_price: 122, suggested_price: 125, change_percent: 2.5, reason: 'Demand + weather' },
      { dish_id: 'd2', dish_name: 'Veg Burger', current_price: 95, suggested_price: 98, change_percent: 3.2, reason: 'Demand high' },
      { dish_id: 'd3', dish_name: 'French Fries', current_price: 85, suggested_price: 82, change_percent: -3.5, reason: 'Schedule' },
    ],
  };
}

function getMockPricingRules(site_id) {
  const rules = [
    { dish_id: 'd1', dish_name: 'Paneer Sandwich', site_id: '*', base_price: 120, min_floor_price: 100, max_surge_price: 150, spread_limit_percent: 15, pricing_mode: 'auto', is_active: true, target_margin_percent: 25, price_rounding_rule: 'nearest_5', created_at: '2025-01-15T10:00:00Z', updated_at: '2025-02-01T12:00:00Z' },
    { dish_id: 'd2', dish_name: 'Veg Burger', site_id: 'site-001', base_price: 95, min_floor_price: 80, max_surge_price: 120, spread_limit_percent: 10, pricing_mode: 'manual', is_active: true, target_margin_percent: null, price_rounding_rule: null, created_at: '2025-01-16T09:00:00Z', updated_at: '2025-01-20T14:00:00Z' },
    { dish_id: 'd3', dish_name: 'French Fries', site_id: '*', base_price: 80, min_floor_price: 70, max_surge_price: 95, spread_limit_percent: 12, pricing_mode: 'auto', is_active: true, target_margin_percent: 20, price_rounding_rule: 'nearest_5', created_at: '2025-01-18T11:00:00Z', updated_at: '2025-02-02T08:00:00Z' },
  ];
  if (site_id) return rules.filter((r) => r.site_id === site_id || r.site_id === '*');
  return rules;
}

function getMockPricingSignals(site_id) {
  return {
    site_id: site_id || null,
    competitor_price_index: 58,
    event_weather_index: 65,
    updated_at: new Date().toISOString(),
  };
}

export default {
  getPricingPreview,
  getPricingAnalytics,
  refreshPricesNow,
  getPricingRules,
  getPricingRule,
  createPricingRule,
  updatePricingRule,
  deletePricingRule,
  getPricingSignals,
  updatePricingSignals,
  getDishesBySite,
  getDishById,
};
